// ── Technology Catalogue (Iteration 3 Part E, §9) ───────────────────────────
// A shared catalogue of external technologies curated by Quantizant. PQC
// status is held per capability; the technology shows a roll-up. Usage is per
// organisation, from Discovery. Table names follow §15 m.

export type Capability =
  | 'tls_termination'
  | 'key_exchange'
  | 'signing'
  | 'key_custody'
  | 'certificate_issuance'
  | 'ssh'
  | 'ipsec_vpn'
  | 'data_at_rest'
  | 'messaging_security'

export const CAPABILITIES: Capability[] = ['tls_termination', 'key_exchange', 'signing', 'key_custody', 'certificate_issuance', 'ssh', 'ipsec_vpn', 'data_at_rest', 'messaging_security']

export const CAPABILITY_LABEL: Record<Capability, string> = {
  tls_termination: 'TLS termination',
  key_exchange: 'Key exchange',
  signing: 'Signing',
  key_custody: 'Key custody',
  certificate_issuance: 'Certificate issuance',
  ssh: 'SSH',
  ipsec_vpn: 'IPsec VPN',
  data_at_rest: 'Data at rest',
  messaging_security: 'Messaging security',
}

export type PqcStatus = 'pqc_ready' | 'partial' | 'roadmap' | 'none' | 'unknown'
export const PQC_STATUSES: PqcStatus[] = ['pqc_ready', 'partial', 'roadmap', 'none', 'unknown']

export type RoadmapConfidence = 'ga' | 'announced' | 'stated' | 'rumoured'
export const ROADMAP_CONFIDENCE_LABEL: Record<RoadmapConfidence, string> = { ga: 'GA', announced: 'Announced', stated: 'Stated', rumoured: 'Rumoured' }

export interface TechnologyVersion {
  version: string
  released_at: string | null
  pqc_version: boolean
}

// catalogue_technology
export interface CatalogueTechnology {
  id: string
  name: string
  vendor_id: string
  vendor: string
  category: string
  description: string
  versions: TechnologyVersion[]
  logo_asset: string | null // monochrome SVG markup or a colour mark; null renders a monogram
  maintained_by: 'quantizant' | string // or the org_id that added it
  created_at: string
  updated_at: string
  updated_by: string
  // Names Discovery reports for this technology (matching detected usage).
  match_names: string[]
}

// catalogue_capability
export interface CatalogueCapability {
  technology_id: string
  capability: Capability
  status: PqcStatus
  algorithms: string[]
  hybrid: boolean
  from_version: string | null
  roadmap_date: string | null // month, yyyy-mm
  roadmap_confidence: RoadmapConfidence | null
  source_url: string | null
  source_note: string | null
  verified_at: string | null
  verified_by: string | null
}

export type UpdateKind = 'verification' | 'detail' | 'roadmap_changed' | 'note'
export const UPDATE_KIND_LABEL: Record<UpdateKind, string> = { verification: 'Verification', detail: 'Detail', roadmap_changed: 'Roadmap changed', note: 'Note' }

// catalogue_update: shared history (org_id null, Quantizant) or org-scoped.
export interface CatalogueUpdate {
  id: string
  technology_id: string
  org_id: string | null
  kind: UpdateKind
  text: string
  author: string
  created_at: string
}

// An organisation's suggested change to a shared field, reviewed by Quantizant.
export interface CatalogueSuggestion {
  id: string
  technology_id: string
  org_id: string
  field: string
  proposed: string
  note: string
  author: string
  created_at: string
  status: 'pending' | 'promoted' | 'declined'
}

// catalogue_usage (derived from Discovery per organisation)
export interface CatalogueUsage {
  technology_id: string
  org_id: string
  asset_id: string
  asset_code: string
  version_detected: string
  component_id: string | null
  system_id: string
  last_seen_at: string | null
}

export interface UnmatchedDetection {
  name: string
  asset_ids: string[]
  system_ids: string[]
}
